import { useEffect, useRef, useState } from 'react'
import { bridge } from '../bridge'
import { copyText } from '../util/clipboard'
import './CopyButton.css'

/// Explicit copy affordance.
///
/// The app disables text selection on <body> for a native feel
/// (styles/global.css), so tapping a button — not long-pressing text — is
/// the primary way anything gets copied out of a screen. Blocks that also
/// opt back into selection keep long-press as a secondary path for lifting
/// a fragment; this covers "give me the whole thing", which is the common
/// case when someone wants to go look a command up before approving it.
///
/// Flips to "Copied" for 1.2s on a confirmed write. On Android 13+ the
/// system shows its own "Copied" chip on every clipboard write, so the flip
/// reads as doubled there — harmless, and not worth a platform branch.
export function CopyButton({
  text,
  label = 'Copy',
  ariaLabel,
  className,
}: {
  text: string
  label?: string
  ariaLabel?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      window.clearTimeout(timer.current)
    }
  }, [])

  function handleClick() {
    void copyText(text).then((ok) => {
      // Only confirm on a write that actually landed.
      if (!ok || !alive.current) return
      bridge.call('haptic.success').catch(() => {})
      setCopied(true)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => {
        if (alive.current) setCopied(false)
      }, 1200)
    })
  }

  return (
    <button
      type="button"
      className={className ?? 'copy-btn'}
      onClick={handleClick}
      aria-label={ariaLabel ?? label}
      aria-live="polite"
    >
      {copied ? 'Copied' : label}
    </button>
  )
}
